
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Menu, X, Home, Brain, Activity, MessageCircle, UserCog
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { 
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { 
  mainMenuItems, 
  patientMenuItems, 
  professionalItems 
} from "./navbar/NavbarMenuItems";
import { UserMenu } from "./navbar/UserMenu";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";
import { NotificationIcon } from "./navbar/NotificationIcon";

const NavLinks = ({ className = "" }: { className?: string }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { isAuthenticated, user } = useAuth();
  const [isProfessional, setIsProfessional] = useState(false);

  useEffect(() => {
    const checkUserType = async () => {
      if (user) {
        try {
          const { data, error } = await supabase
            .from('profiles')
            .select('type')
            .eq('id', user.id)
            .single();
            
          if (error) {
            console.error('Error fetching user type:', error);
            return;
          }
          
          setIsProfessional(data?.type === 'professional');
        } catch (error) {
          console.error('Error checking user type:', error);
        }
      }
    };
    
    checkUserType();
  }, [user]);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handlePatientMenuClick = (path: string) => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    navigate(path);
  };

  if (isMobile) {
    return (
      <div className={cn("flex gap-1", className)}>
        <Button 
          variant={isActive("/") ? "default" : "ghost"} 
          asChild 
          size="sm"
          className={cn(
            "gap-1.5",
            isActive("/") ? "bg-blue-600 hover:bg-blue-700 text-white" : "text-gray-800"
          )}
        >
          <Link to="/">
            <Home className="h-4 w-4" />
            <span>Accueil</span>
          </Link>
        </Button>

        <Button 
          variant={isActive("/ai-assistance") ? "default" : "ghost"} 
          asChild 
          size="sm"
          className={cn(
            "gap-1.5",
            isActive("/ai-assistance") ? "bg-blue-600 hover:bg-blue-700 text-white" : "text-gray-800"
          )}
        >
          <Link to="/ai-assistance">
            <Brain className="h-4 w-4" />
            <span>Assistant IA</span>
          </Link>
        </Button>
        
        <Button 
          variant={isActive("/realtime-ai-assistant") ? "default" : "ghost"} 
          asChild 
          size="sm"
          className={cn(
            "gap-1.5",
            isActive("/realtime-ai-assistant") ? "bg-blue-600 hover:bg-blue-700 text-white" : "text-gray-800"
          )}
        >
          <Link to="/realtime-ai-assistant">
            <Activity className="h-4 w-4" />
            <span>Assistant Temps Réel</span>
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <NavigationMenu className={cn("z-30", className)}>
      <NavigationMenuList>
        {/* Main Menu Items */}
        {mainMenuItems.map((item) => (
          <NavigationMenuItem key={item.path}>
            <NavigationMenuLink asChild>
              <Link 
                to={item.path}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md",
                  isActive(item.path) ? "text-blue-600" : "text-gray-700 hover:text-blue-600"
                )}
              >
                <item.icon className="h-4 w-4" />
                <span>{item.label}</span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>
        ))}

        {/* Patient Menu - Always visible for all users */}
        {patientMenuItems.map((category) => (
          <NavigationMenuItem key={category.label}>
            <NavigationMenuTrigger className="flex items-center gap-1.5">
              <category.icon className="h-4 w-4" />
              <span>{category.label}</span>
            </NavigationMenuTrigger>
            <NavigationMenuContent>
              <div className="grid w-[400px] gap-3 p-4">
                {category.items.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => handlePatientMenuClick(item.path)}
                    className="flex items-center gap-2 rounded-md p-2 hover:bg-blue-50 w-full text-left"
                  >
                    <div className="bg-blue-100 p-1.5 rounded-md">
                      <item.icon className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium">{item.label}</div>
                    </div>
                  </button>
                ))}
              </div>
            </NavigationMenuContent>
          </NavigationMenuItem>
        ))}

        {/* Professional Dashboard Button - Only show for professional users */}
        {isProfessional && (
          <NavigationMenuItem>
            <NavigationMenuLink asChild>
              <Link 
                to="/professional"
                className={cn(
                  "flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md",
                  isActive('/professional') ? "text-blue-600" : "text-gray-700 hover:text-blue-600"
                )}
              >
                {React.createElement(professionalItems[0].icon, { className: "h-4 w-4" })}
                <span>{professionalItems[0].label}</span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>
        )}
      </NavigationMenuList>
    </NavigationMenu>
  );
};

export function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isMobile = useIsMobile();

  return (
    <header className="sticky top-0 z-40 w-full border-b border-b-slate-200 bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/60">
      <div className="container flex h-16 items-center">
        {/* Left section: Logo */}
        <div className="flex-none">
          <Link to="/" className="flex items-center gap-2 text-blue-600 hover:text-blue-800">
            <span className="text-xl font-bold">Medicace</span>
          </Link>
        </div>

        {isMobile ? (
          <>
            <div className="flex-1"></div>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Toggle Menu"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="flex-none"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>

            {isMenuOpen && (
              <div className="absolute top-16 left-0 right-0 bg-white border-b border-gray-200 p-4 shadow-md">
                <NavLinks className="flex-col items-start gap-2" />
              </div>
            )}
          </>
        ) : (
          <>
            {/* Center section: Menu items */}
            <div className="flex-1 flex justify-center">
              <NavLinks />
            </div>
            
            {/* Right section: User icons */}
            <div className="flex-none flex items-center gap-2">
              <NotificationIcon />
              <Button
                variant="ghost"
                size="icon"
                asChild
                className="text-gray-700 hover:text-blue-600"
              >
                <Link to="/messages">
                  <MessageCircle className="h-5 w-5" />
                </Link>
              </Button>
              <Button
                variant="ghost"
                size="icon"
                asChild
                className="text-gray-700 hover:text-blue-600"
              >
                <Link to="/profile">
                  <UserCog className="h-5 w-5" />
                </Link>
              </Button>
            </div>
          </>
        )}
      </div>
    </header>
  );
}
